import { Bell, X, Search, Send } from "lucide-react";

const AVATAR_COLORS = ["#572B7C", "#78287C", "#28C0D0", "#0F766E", "#2563EB", "#15803D", "#D97706", "#0E7490"];
const initials = (n) => {
  const p = (n || "").trim().split(/\s+/);
  return (((p[0] || "")[0] || "") + ((p[1] || "")[0] || "")).toUpperCase() || "?";
};

const platformBadges = (platform) => {
  const out = [];
  const has = (k) => (platform || "").includes(k);
  if (has("ios")) out.push({ label: "iOS", fg: "#0C1120", bg: "#E6E9EF" });
  if (has("android")) out.push({ label: "Android", fg: "#0F766E", bg: "#CCFBF1" });
  if (out.length === 0 && platform) out.push({ label: platform, fg: "#475569", bg: "#E2E8F0" });
  return out;
};

const PushNotificationModals = ({
  showPushModal,
  setShowPushModal,
  pushRegCount,
  pushRegUsers,
  pushRegPage,
  pushRegTotalPages,
  pushRegSearch,
  setPushRegSearch,
  loadingPushReg,
  fetchPushRegUsers,
  showSendPushModal,
  setShowSendPushModal,
  sendPushUser,
  setSendPushUser,
  pushTitle,
  setPushTitle,
  pushMessage,
  setPushMessage,
  pushLinkUrl,
  setPushLinkUrl,
  sendingPush,
  handleSendPush,
}) => {
  const closePushList = () => { setShowPushModal(false); setPushRegSearch(""); };
  const closeSendPush = () => { setShowSendPushModal(false); setSendPushUser(null); setPushTitle(""); setPushMessage(""); setPushLinkUrl && setPushLinkUrl(""); };

  return (
    <>
      {/* Push Registrations Modal */}
      {showPushModal && (
        <div
          className="sz-modal-backdrop"
          style={{ zIndex: 60 }}
          onClick={(e) => { if (e.target === e.currentTarget) closePushList(); }}
        >
          <div className="sz-modal-card w-full" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex flex-none items-center justify-between border-b border-[#EEF0F4] px-[22px] py-[18px]">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#EDE4F4] text-[#78287C]">
                  <Bell className="h-[19px] w-[19px]" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-[16px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
                    Push Registrations
                  </h3>
                  <div className="text-[12px] text-[#8A90A2]">{pushRegCount} devices</div>
                </div>
              </div>
              <button
                onClick={closePushList}
                className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[#F1F3F7] text-[16px] text-[#2B3142] transition-colors hover:bg-[#E6E9EF]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Search */}
            <div className="flex-none px-[22px] pb-2.5 pt-3.5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8AEBC]" strokeWidth={2} />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={pushRegSearch}
                  onChange={(e) => setPushRegSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchPushRegUsers(1, pushRegSearch); }}
                  className="sz-input !pl-9 !pr-20"
                />
                <button
                  onClick={() => fetchPushRegUsers(1, pushRegSearch)}
                  className="sz-btn absolute right-1.5 top-1/2 -translate-y-1/2 !px-3 !py-1.5 !text-[12px] !text-white"
                  style={{ background: "#78287C" }}
                >
                  Search
                </button>
              </div>
            </div>

            {/* List */}
            <div className="szscroll flex-1 overflow-y-auto px-3.5 pb-2.5 pt-0.5">
              {loadingPushReg ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#78287C]" />
                </div>
              ) : pushRegUsers.length === 0 ? (
                <div className="px-5 py-10 text-center text-[13px] text-[#8A90A2]">
                  No devices match {pushRegSearch ? `“${pushRegSearch}”` : "your search"}.
                </div>
              ) : (
                pushRegUsers.map((user, i) => {
                  const badges = platformBadges(user.platform);
                  return (
                    <div key={user.id ?? i} className="flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors hover:bg-[#F7F8FB]">
                      {user.profile_image ? (
                        <img src={user.profile_image} alt={user.full_name} className="h-[38px] w-[38px] flex-none rounded-full object-cover" />
                      ) : (
                        <div
                          className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full text-[12.5px] font-bold text-white"
                          style={{ fontFamily: "Poppins,sans-serif", background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                        >
                          {initials(user.full_name || user.email)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-[#0C1120]">{user.full_name || "Unknown"}</div>
                        <div className="truncate text-[11.5px] text-[#8A90A2]">{user.email}</div>
                      </div>
                      <div className="flex flex-none gap-1.5">
                        {badges.map((b, bi) => (
                          <span
                            key={bi}
                            className="rounded-md px-2 py-[3px] text-[10px] font-bold"
                            style={{ fontFamily: "Poppins,sans-serif", color: b.fg, background: b.bg }}
                          >
                            {b.label}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => { setSendPushUser(user); setShowSendPushModal(true); }}
                        className="sz-btn flex-none !rounded-[9px] !px-3 !py-[7px] !text-[12px] !text-white"
                        style={{ background: "#78287C" }}
                        title="Send push notification"
                      >
                        <Send className="h-[13px] w-[13px]" strokeWidth={2} />
                        Send
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {pushRegTotalPages > 1 && (
              <div className="flex flex-none items-center justify-between border-t border-[#EEF0F4] px-[22px] py-3">
                <span className="text-[12px] text-[#8A90A2]">Page {pushRegPage} of {pushRegTotalPages}</span>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => fetchPushRegUsers(pushRegPage - 1, pushRegSearch)}
                    disabled={pushRegPage <= 1}
                    className="sz-btn sz-btn-outline !px-3 !py-[7px] !text-[12.5px]"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => fetchPushRegUsers(pushRegPage + 1, pushRegSearch)}
                    disabled={pushRegPage >= pushRegTotalPages}
                    className="sz-btn sz-btn-outline !px-3 !py-[7px] !text-[12.5px]"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send Push Notification Modal */}
      {showSendPushModal && sendPushUser && (
        <div
          className="sz-modal-backdrop"
          style={{ zIndex: 70 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeSendPush(); }}
        >
          <div className="sz-modal-card w-full" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex flex-none items-center justify-between border-b border-[#EEF0F4] px-[22px] py-[18px]">
              <h3 className="text-[16px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
                Send Push Notification
              </h3>
              <button
                onClick={closeSendPush}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-[#F1F3F7] text-[#2B3142] transition-colors hover:bg-[#E6E9EF]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="szscroll overflow-y-auto px-[22px] py-[18px]">
              {/* Recipient chip */}
              <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-[#EDE4F4] bg-[#FAF7FC] px-3.5 py-3">
                <div
                  className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full text-[12px] font-bold text-white"
                  style={{ fontFamily: "Poppins,sans-serif", background: "linear-gradient(135deg,#572B7C,#78287C)" }}
                >
                  {initials(sendPushUser.full_name || sendPushUser.email)}
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#0C1120]">{sendPushUser.full_name || "Unknown"}</div>
                  <div className="truncate text-[11.5px] text-[#8A90A2]">{sendPushUser.email}</div>
                </div>
              </div>

              {/* Title */}
              <label className="mb-1.5 block text-[12px] font-semibold text-[#2B3142]">
                Title <span className="font-medium text-[#A8AEBC]">({pushTitle.length}/50)</span>
              </label>
              <input
                type="text"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                maxLength={50}
                placeholder="New job near you"
                className="sz-input mb-3.5"
              />

              {/* Message */}
              <label className="mb-1.5 block text-[12px] font-semibold text-[#2B3142]">
                Message <span className="font-medium text-[#A8AEBC]">({pushMessage.length}/200)</span>
              </label>
              <textarea
                value={pushMessage}
                onChange={(e) => setPushMessage(e.target.value)}
                maxLength={200}
                rows={3}
                placeholder="A wedding photography job just opened in your area — tap to view details."
                className="sz-input mb-4 resize-none"
                style={{ height: 78 }}
              />

              {/* Link URL (optional) — opens on tap */}
              <label className="mb-1.5 block text-[12px] font-semibold text-[#2B3142]">
                Link URL <span className="font-medium text-[#A8AEBC]">(optional — opens when tapped)</span>
              </label>
              <input
                type="url"
                value={pushLinkUrl || ""}
                onChange={(e) => setPushLinkUrl && setPushLinkUrl(e.target.value)}
                placeholder="https://apps.apple.com/app/shootzu/id6756085766"
                className="sz-input mb-4"
              />

              {/* Preview */}
              <div className="mb-[18px]">
                <div className="mb-[7px] text-[11px] font-semibold uppercase tracking-[.3px] text-[#8A90A2]">Preview</div>
                <div className="flex gap-2.5 rounded-[13px] bg-[#F1F3F7] px-3.5 py-3">
                  <div
                    className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] text-white"
                    style={{ background: "linear-gradient(135deg,#572B7C,#78287C)" }}
                  >
                    <Bell className="h-[17px] w-[17px]" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
                      {pushTitle || "New job near you"}
                    </div>
                    <div className="mt-[2px] text-[12px] leading-[1.4] text-[#2B3142]">
                      {pushMessage || "A wedding photography job just opened in your area — tap to view details."}
                    </div>
                    <div className="mt-1 text-[10.5px] text-[#A8AEBC]">Shootzu · now</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-2.5">
                <button onClick={closeSendPush} className="sz-btn sz-btn-outline flex-1">Cancel</button>
                <button
                  onClick={handleSendPush}
                  disabled={!pushTitle.trim() || !pushMessage.trim() || sendingPush}
                  className="sz-btn flex-[1.4] !text-white"
                  style={{ background: "#78287C" }}
                >
                  {sendingPush ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-[15px] w-[15px]" strokeWidth={2} />
                      Send notification
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PushNotificationModals;
